import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faBriefcase,
  faClock,
  faIdBadge,
  faCloudSunRain,
  faClipboardCheck,
  faDrawPolygon,
  faGasPump,
  faLocationCrosshairs,
  faPlane,
  faRoute,
} from '@fortawesome/free-solid-svg-icons'

import {
  DEFAULT_OPERATION_INPUTS,
  entityRef,
  operatorLabel,
  parseStepPointRef,
  stepPointRef,
  type OperationInputs,
  type OperationStep,
  type OperatorDef,
  type OperatorId,
} from '../operations'

export const OPERATOR_ICON: Record<OperatorId, IconDefinition> = {
  eta: faRoute,
  nearby: faLocationCrosshairs,
  'air-miles': faPlane,
  contains: faDrawPolygon,
  'fuel-planning': faGasPump,
  'local-license-check': faClipboardCheck,
  forecast: faBriefcase,
  'weather-forecast': faCloudSunRain,
  time: faClock,
  'cert-check': faIdBadge,
}

function toLocalYmd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function defaultForecastDates(): { forecastStartDate: string; forecastEndDate: string } {
  const start = new Date()
  const end = new Date()
  end.setDate(end.getDate() + 30)
  return {
    forecastStartDate: start.toISOString().slice(0, 10),
    forecastEndDate: end.toISOString().slice(0, 10),
  }
}

/** Current calendar week (Sunday → today) for the Time operator. */
export function defaultTimeDates(): { timeStartDate: string; timeEndDate: string } {
  const today = new Date()
  const start = new Date(today)
  start.setDate(start.getDate() - start.getDay())
  return {
    timeStartDate: toLocalYmd(start),
    timeEndDate: toLocalYmd(today),
  }
}

/** True when destinationEntityRef points at a map result on the given step. */
export function destinationTargetsStepResult(
  inputs: OperationInputs,
  step: OperationStep | undefined,
): boolean {
  if (!step?.result) return false
  const ref = inputs.destinationEntityRef.trim()
  if (!ref) return false
  const parsed = parseStepPointRef(ref)
  if (parsed) {
    if (parsed.fromStepId !== step.id) return false
    return step.result.mapPoints.some((point) => point.id === parsed.pointId)
  }
  // Catalog entity refs still count when that entity appears in this step's results.
  // (e.g. dragging a Nearby asset that was encoded as `asset:…` instead of stepPoint).
  return step.result.mapPoints.some((point) => {
    if (!point.sourceEntity) return false
    return entityRef(point.sourceEntity.category, point.sourceEntity.id) === ref
  })
}

/** Destination options from operation map results through `throughIndex` (inclusive). */
export function destinationOptionsFromStepResults(
  steps: OperationStep[],
  throughIndex: number,
  activeStepId: string,
): Array<{ id: string; label: string; group: string; keywords?: string }> {
  const options: Array<{ id: string; label: string; group: string; keywords?: string }> = []
  const end = Math.min(throughIndex, steps.length - 1)
  for (let i = 0; i <= end; i += 1) {
    const step = steps[i]
    const points = step.result?.mapPoints ?? []
    if (points.length === 0) continue
    const stepLabel = operatorLabel(step.operatorId) || step.result?.title || `Step ${i + 1}`
    const group =
      step.id === activeStepId
        ? `Current results · ${stepLabel}`
        : `Step ${i + 1} · ${stepLabel}`
    for (const point of points) {
      options.push({
        id: stepPointRef(step.id, point.id),
        label: point.subtitle ? `${point.name} · ${point.subtitle}` : point.name,
        group,
        keywords: [point.name, point.subtitle, stepLabel].filter(Boolean).join(' '),
      })
    }
  }
  return options
}

export function areOperatorArgsComplete(op: OperatorDef, inputs: OperationInputs): boolean {
  for (const arg of op.args) {
    if (arg === 'destination') {
      if (op.id === 'fuel-planning') continue
      if (inputs.destinationMode === 'address') {
        if (!inputs.destinationAddress.trim()) return false
      } else if (!inputs.destinationEntityRef.trim()) {
        return false
      }
    }
    if (arg === 'nearbyTarget' && !String(inputs.nearbyTarget || '').trim()) return false
    if (arg === 'radiusMiles' && !(inputs.radiusMiles > 0)) return false
    if (arg === 'rangeMiles' && !(inputs.rangeMiles > 0)) return false
    if (arg === 'forecastType' && !inputs.forecastType) return false
    if (arg === 'forecastStartDate' && !/^\d{4}-\d{2}-\d{2}$/.test(inputs.forecastStartDate)) {
      return false
    }
    if (arg === 'forecastEndDate' && !/^\d{4}-\d{2}-\d{2}$/.test(inputs.forecastEndDate)) {
      return false
    }
    if (arg === 'timeStartDate' && !/^\d{4}-\d{2}-\d{2}$/.test(inputs.timeStartDate)) {
      return false
    }
    if (arg === 'timeEndDate' && !/^\d{4}-\d{2}-\d{2}$/.test(inputs.timeEndDate)) {
      return false
    }
  }
  if (
    op.args.includes('forecastStartDate') &&
    op.args.includes('forecastEndDate') &&
    inputs.forecastStartDate > inputs.forecastEndDate
  ) {
    return false
  }
  if (
    op.args.includes('timeStartDate') &&
    op.args.includes('timeEndDate') &&
    inputs.timeStartDate > inputs.timeEndDate
  ) {
    return false
  }
  return true
}

export function initialInputsForOperator(op: OperatorDef, preferredDestinationRef = ''): OperationInputs {
  const forecastDates = defaultForecastDates()
  const timeDates = defaultTimeDates()
  return {
    ...DEFAULT_OPERATION_INPUTS,
    ...forecastDates,
    ...timeDates,
    destinationMode: 'entity',
    destinationEntityRef: preferredDestinationRef,
    nearbyTarget: 'assets',
  }
}

